import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import {
  MsalService,
  MsalModule,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CommonModule,
    MsalModule,
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
  ],
  standalone: true
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Las Pilas';
  isIframe = false;
  loginDisplay = false;
  private originalTitle = '';
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private meta: Meta
  ) {}

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();

    this.isIframe = window !== window.parent && !window.opener;

    // Configurar indexabilidad según entorno
    this.configureRobotsMetaTag();

    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

  // Guardar title original y configurar cambio dinámico de favicons y títulos según ruta
  this.originalTitle = (typeof document !== 'undefined' && document.title) ? document.title : this.title || 'Wiki Digital';
  this.setupFaviconSwitcher();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService
        .loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * Activa un listener de router para alternar favicons entre web y CMS
   */
  private setupFaviconSwitcher(): void {
    if (typeof document === 'undefined') return;
    // Estado inicial
    this.updateFaviconsForUrl(this.router.url);
    // Suscripción a cambios de navegación
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe((e) => this.updateFaviconsForUrl(e.urlAfterRedirects || e.url));
  }

  /** Detecta si la URL pertenece al admin/CMS */
  private isAdminUrl(url: string): boolean {
    try {
      // Normalizar solo el path
      const path = url || window.location.pathname || '';
      return /(\/_?admin|\/private-app)(\/|$)/.test(path);
    } catch {
      return false;
    }
  }

  /**
   * Cambia href de los link rel icon/apple-touch-icon y meta TileImage
   */
  private updateFaviconsForUrl(url: string): void {
    // También actualizar título y metas relacionadas
    this.updateTitlesForUrl(url);
    const useCms = this.isAdminUrl(url);
    const targetBase = useCms ? '/assets/img/favicons-cms/' : '/assets/img/favicons/';
    const pattern = /\/assets\/img\/favicons(-cms)?\//;

    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]'));
    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const newHref = href.replace(pattern, targetBase);
      if (newHref !== href) this.replaceLinkElement(link, newHref);
    });

    // MS Tile image
    const tileMeta = document.querySelector<HTMLMetaElement>('meta[name="msapplication-TileImage"]');
    if (tileMeta) {
      const content = tileMeta.getAttribute('content') || '';
      const newContent = content.replace(pattern, targetBase);
      if (newContent !== content) tileMeta.setAttribute('content', newContent);
    }
  }

  /** Actualiza document.title y metas title/og:title/twitter:title según si estamos en admin */
  private updateTitlesForUrl(url: string): void {
    try {
      const useCms = this.isAdminUrl(url);
      const adminTitle = 'Admin - Our Anthology';
      const newTitle = useCms ? adminTitle : (this.originalTitle || this.title || 'Our Anthology');

      if (typeof document !== 'undefined') {
        if (document.title !== newTitle) {
          document.title = newTitle;
        }

        const metaSelectors = [
          'meta[name="title"]',
          'meta[property="og:title"]',
          'meta[name="twitter:title"]'
        ];

        metaSelectors.forEach((sel) => {
          const meta = document.querySelector<HTMLMetaElement>(sel);
          if (!meta) return;
          const content = meta.getAttribute('content') || '';
          if (content !== newTitle) meta.setAttribute('content', newTitle);
        });
      }
    } catch {
      // No queremos romper la app si hay problemas con el DOM
    }
  }

  /** Reemplaza el <link> para forzar recarga del favicon en navegadores */
  private replaceLinkElement(link: HTMLLinkElement, newHref: string): void {
    try {
      const newLink = link.cloneNode() as HTMLLinkElement;
      newLink.setAttribute('href', newHref);
      link.parentNode?.insertBefore(newLink, link.nextSibling);
      link.remove();
    } catch {
      link.setAttribute('href', newHref);
    }
  }

  /** Configura el meta tag robots según el entorno */
  private configureRobotsMetaTag(): void {
    try {
      console.log('🤖 Configurando robots meta tag para entorno:', environment.indexable ? 'indexable' : 'no indexable');

      // Eliminar cualquier meta robots existente
      const existingRobots = document.querySelectorAll('meta[name="robots"]');
      existingRobots.forEach(meta => meta.remove());

      // Si el entorno no es indexable, agregar noindex, nofollow
      if (!environment.indexable) {
        console.log('🚫 Entorno no indexable - agregando noindex, nofollow');
        this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
      } else {
        console.log('✅ Entorno indexable - sin restricciones de robots');
      }
    } catch (error) {
      console.error('❌ Error configurando robots meta tag:', error);
    }
  }
}
